/**
 * Agent Fiona's card lifecycle (AGS-896), as the dashboard sees it.
 *
 * Every Fiona card in GET /rule-engine-decisions carries fiona_card:
 * { decision_id, status, card_type, show_again_at, status_changed_at,
 *   decided_by, answered_decision_id }. Rejected and closed cards are never
 * returned, so the statuses seen here are:
 * - open: not answered yet
 * - checking_with_manufacturer, in_manufacturing, awaiting_remeasurement:
 *   accepted, at that stage (packaging cards), with a follow-up date
 * - finished, shown as "Resolved": the change is done, waiting to be marked
 *   as reviewed. It has no follow-up date.
 *
 * The tab comes from the status and show_again_at. The backend does nothing
 * when that date passes: an accepted card simply moves back to "Open"
 * here once it's due.
 */

const STATUS_OPEN = "open";
export const STATUS_FINISHED = "finished";

const ACCEPTED_STAGES = [
  "checking_with_manufacturer",
  "in_manufacturing",
  "awaiting_remeasurement",
];

const STATUS_LABELS = {
  open: "Open",
  checking_with_manufacturer: "Checking with manufacturer",
  in_manufacturing: "In manufacturing",
  awaiting_remeasurement: "Awaiting remeasurement",
  finished: "Resolved",
};

export const statusLabel = (status) => STATUS_LABELS[status] || status || "";

const pad = (n) => String(n).padStart(2, "0");

// Local calendar date as YYYY-MM-DD: follow-ups are days, not instants
const toIsoDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const todayIso = (now = new Date()) => toIsoDate(now);

// "2026-11-16T00:00:00" → "2026-11-16"
export const datePart = (value) =>
  /^\d{4}-\d{2}-\d{2}/.exec(value || "")?.[0] || null;

// "2026-11-16" → "16 Nov 2026", built from the parts so it doesn't shift a
// day west of UTC
export const formatFionaDate = (value) => {
  const iso = datePart(value);
  if (!iso) return "";
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const isAcceptedStage = (status) => ACCEPTED_STAGES.includes(status);

// An accepted card whose follow-up date is today or earlier. One with no
// date is treated as due, so it can't get lost in "Open".
export const isFollowUpDue = (fionaCard, today = todayIso()) => {
  if (!isAcceptedStage(fionaCard?.status)) return false;
  const followUp = datePart(fionaCard.show_again_at);
  return !followUp || followUp <= today;
};

// "review", "open", "outcome", or null for a status the dashboard doesn't
// show. A card without fiona_card has never been answered, so it's open.
export const getFionaTab = (decision, today = todayIso()) => {
  const fionaCard = decision?.fiona_card;
  const status = fionaCard?.status || STATUS_OPEN;
  if (status === STATUS_OPEN) return "review";
  if (status === STATUS_FINISHED) return "outcome";
  if (isAcceptedStage(status)) {
    return isFollowUpDue(fionaCard, today) ? "review" : "open";
  }
  return null;
};

// What GET /fiona-cards/options returns today, used until it loads or if it
// fails. Mirrors chalicelib/utils/fiona_cards.py in store_management.
export const DEFAULT_FIONA_OPTIONS = {
  accepted_statuses: {
    adjust_packaging: [...ACCEPTED_STAGES, STATUS_FINISHED],
    // Asking Amazon to measure again: the only stage is waiting for it
    request_remeasurement: ["awaiting_remeasurement", STATUS_FINISHED],
  },
  closed_on_accept: ["adjust_price"],
  reject_reasons: [
    {
      code: "not_feasible",
      label: "Not feasible",
      note_required: true,
      all_marketplaces: false,
      excludes: null,
    },
    {
      code: "discontinued",
      label: "Discontinued",
      note_required: false,
      all_marketplaces: true,
      excludes: "asin",
    },
    {
      code: "not_sold_in_marketplace",
      label: "Not sold in this marketplace",
      note_required: false,
      all_marketplaces: false,
      excludes: "asin_marketplace",
    },
    {
      code: "other",
      label: "Other",
      note_required: true,
      all_marketplaces: false,
      excludes: null,
    },
  ],
};

// A remeasurement card: Amazon bills a bigger size or weight than our own
// record of the product, so the fix is asking Amazon to measure it again
const CARD_TYPE_REMEASUREMENT = "request_remeasurement";

export const cardTypeOf = (decision) => {
  if (decision?.fiona_card?.card_type) return decision.fiona_card.card_type;
  const action = (decision?.decision?.action || "").toLowerCase();
  if (action.includes("remeasure")) return CARD_TYPE_REMEASUREMENT;
  return action.includes("price") ? "adjust_price" : "adjust_packaging";
};

export const isRemeasurementCard = (decision) =>
  cardTypeOf(decision) === CARD_TYPE_REMEASUREMENT;

// The statuses an accepted card can move to, Resolved (finished) included.
// The backend's options win; a card type they don't list yet (remeasurement)
// falls back to the defaults here.
export const updateStatusesFor = (options, cardType) =>
  options?.accepted_statuses?.[cardType] ||
  DEFAULT_FIONA_OPTIONS.accepted_statuses[cardType] ||
  [];

// The stages a card can be accepted into: never straight to Resolved
export const acceptStagesFor = (options, cardType) =>
  updateStatusesFor(options, cardType).filter(
    (status) => status !== STATUS_FINISHED
  );

// A price-only card closes as soon as it's accepted: no stage, no follow-up
export const isClosedOnAccept = (options, cardType) =>
  (options?.closed_on_accept || []).includes(cardType);

// What each reason hides, from the backend's rules
export const rejectEffect = (reason) => {
  if (reason?.excludes === "asin")
    return "Hides every card for this product, in every marketplace.";
  if (reason?.excludes === "asin_marketplace")
    return "Hides every card for this product in this marketplace.";
  return "Hides this card.";
};

// Rejecting with a reason that excludes the product hides all its Fiona
// cards on screen (both card groups), not only the one answered
export const rejectHidesProduct = (reason) => Boolean(reason?.excludes);

export const FOLLOW_UP_OPTIONS = [
  { key: "1w", label: "In 1 week", days: 7 },
  { key: "2w", label: "In 2 weeks", days: 14 },
  { key: "1m", label: "In 1 month", months: 1 },
  { key: "2m", label: "In 2 months", months: 2 },
  { key: "3m", label: "In 3 months", months: 3 },
  { key: "date", label: "Pick a date" },
];

// The follow-up suggested for each stage, until the user picks one
export const DEFAULT_FOLLOW_UP = {
  checking_with_manufacturer: "1m",
  in_manufacturing: "3m",
  awaiting_remeasurement: "1m",
};

// The date a follow-up option lands on, as YYYY-MM-DD. Month steps clamp to
// the month's last day (31 Jan + 1 month = 28/29 Feb).
export const followUpDate = (key, today = todayIso()) => {
  const option = FOLLOW_UP_OPTIONS.find((o) => o.key === key);
  if (!option || option.key === "date") return null;
  const [year, month, day] = today.split("-").map(Number);
  if (option.days) {
    return toIsoDate(new Date(year, month - 1, day + option.days));
  }
  const target = new Date(year, month - 1 + option.months, 1);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0
  ).getDate();
  target.setDate(Math.min(day, lastDay));
  return toIsoDate(target);
};

// A card from a /fiona-cards action response, applied to the decision's
// fiona_card. decision_id stays the shown decision's; the response's
// rule_engine_decision_id is the one that was answered.
export const mergeFionaCard = (fionaCard, changed) => ({
  ...fionaCard,
  status: changed.status,
  card_type: changed.card_type || fionaCard?.card_type,
  show_again_at: changed.show_again_at,
  status_changed_at: changed.status_changed_at,
  decided_by: changed.decided_by,
  answered_decision_id:
    changed.rule_engine_decision_id ?? fionaCard?.answered_decision_id,
});

// Whether the dashboard drops a card with this status
export const isGoneStatus = (status) =>
  status === "closed" || status === "rejected";
